import dayjs, { Dayjs } from "dayjs"
import { cloneDeep, orderBy } from "lodash"

import { Student } from "#classes/Student"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import {
  StudentConstructor,
  StudentSkillHistory,
} from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { AttendanceClassSessions, AttendanceStatus } from "#customTypes/types"

import { isAttendanceStatusMutable } from "./classHelpers"
import { isTodayOrFuture } from "./dateHelpers"

// We need to sort by name but list students with dob
export const getStudentOptions = (
  students: StudentConstructor[] | null | undefined
): { key: string; label: string; value: string }[] => {
  if (!students || students.length === 0) return []

  const studentsWithNameAndDob = orderBy(students, "name", "asc")?.map(
    (studentConstructor) => {
      const student = new Student(studentConstructor)

      return {
        key: student.id,
        label: student.getNameAndDob(),
        value: student.id,
      }
    }
  )

  return studentsWithNameAndDob
}

export const getBlankAttendance = (
  sessionIds: Uuid[]
): AttendanceClassSessions => {
  return sessionIds.map((sessionId) => ({
    status: ATTENDANCE_STATUSES.NOT_ENROLLED,
    sessionId,
  }))
}

/**
 * Remaps ABSENT status to ENROLLED for future sessions.
 * This is a workaround for a database issue where future attendance was marked as ABSENT.
 * Only affects display/validation, does not modify the actual database values.
 */
export const remapAbsentForFutureSessions = (
  status: AttendanceStatus,
  sessionDate: Dayjs | string | number,
  now: Dayjs
): AttendanceStatus => {
  if (status !== ATTENDANCE_STATUSES.ABSENT) {
    return status
  }

  const date = dayjs(sessionDate)

  // If session is in the future (on or after today), remap ABSENT to ENROLLED
  if (isTodayOrFuture(date, now)) {
    return ATTENDANCE_STATUSES.ENROLLED
  }

  return status
}

/**
 * Returns true if any session has a status other than NOT_ENROLLED.
 * Used to decide whether to keep or delete the attendance key for a class.
 * Tryout/makeup statuses count — we only delete when everything is NOT_ENROLLED.
 */
export const isStillEnrolled = (
  attendanceForClass: { status: AttendanceStatus }[]
) => {
  return attendanceForClass.some(
    ({ status }) =>
      status !== ATTENDANCE_STATUSES.NOT_ENROLLED &&
      status !== ATTENDANCE_STATUSES.INVALID
  )
}

/**
 * Tryout and makeup statuses don't count as real enrollment.
 * Used to determine whether to send a new enrollment or renewal receipt.
 */
const NON_REAL_ENROLLMENT_STATUSES = [
  ATTENDANCE_STATUSES.NOT_ENROLLED,
  ATTENDANCE_STATUSES.INVALID,
  ATTENDANCE_STATUSES.TRYOUT_PRESENT,
  ATTENDANCE_STATUSES.TRYOUT_ABSENT,
  ATTENDANCE_STATUSES.TRYOUT_TARDY,
  ATTENDANCE_STATUSES.TRYOUT_ENROLLED,
  ATTENDANCE_STATUSES.MAKEUP_PRESENT,
  ATTENDANCE_STATUSES.MAKEUP_ABSENT,
  ATTENDANCE_STATUSES.MAKEUP_TARDY,
  ATTENDANCE_STATUSES.MAKEUP_ENROLLED,
] as const

/**
 * Checks if a student has "real" enrollment history in a class.
 * Excludes tryout/makeup statuses, NOT_ENROLLED, and INVALID.
 * Used to determine whether to send a new enrollment or renewal receipt.
 */
export const hasRealEnrollmentHistory = (
  attendanceForClass: { status: AttendanceStatus }[] | undefined
): boolean => {
  if (!attendanceForClass?.length) return false

  return attendanceForClass.some(({ status }) => {
    if (!status) return false
    return !(NON_REAL_ENROLLMENT_STATUSES as readonly string[]).includes(status)
  })
}

export const getNewAttendanceForClass = (
  attendanceForClass: AttendanceClassSessions,
  selectedSessions: { isSelected: boolean; sessionId: Uuid }[]
) => {
  const newAttendanceForClass = selectedSessions.map(
    ({ isSelected, sessionId }, i) => {
      const existingStatus = attendanceForClass[i]?.status

      // Don't modify if status has already been recorded
      const isValid = isAttendanceStatusMutable(existingStatus)

      if (!isValid) {
        return {
          status: existingStatus,
          sessionId,
        }
      }

      // If session is deselected, always set to NOT_ENROLLED
      if (!isSelected) {
        return { status: ATTENDANCE_STATUSES.NOT_ENROLLED, sessionId }
      }

      // If session was NOT_ENROLLED but is now selected, set to ENROLLED
      if (isSelected && existingStatus === ATTENDANCE_STATUSES.NOT_ENROLLED) {
        return { status: ATTENDANCE_STATUSES.ENROLLED, sessionId }
      }

      // If session is selected and has a valid status, keep it
      return {
        status: existingStatus ?? ATTENDANCE_STATUSES.ENROLLED,
        sessionId,
      }
    }
  )

  // Delete attendance if no longer enrolled
  if (!isStillEnrolled(newAttendanceForClass)) {
    return null
  }

  return newAttendanceForClass
}

export const addNewSkillEntry = (
  skillHistory: StudentSkillHistory,
  semester: string,
  entry: Partial<StudentSkillHistory[string]>
): StudentSkillHistory => {
  const newHistory = cloneDeep(skillHistory)

  const thisSemester: StudentSkillHistory[string] = {
    ...newHistory[semester],
    ...entry,
  }

  return {
    ...skillHistory,
    [semester]: thisSemester,
  }
}

/**
 * Statuses that count as "attended" for project-day tracking.
 * TRYOUT_* statuses never count toward project progress.
 */
export const ATTENDED_PROJECT_STATUSES: ReadonlySet<AttendanceStatus> =
  new Set<AttendanceStatus>([
    ATTENDANCE_STATUSES.PRESENT,
    ATTENDANCE_STATUSES.TARDY,
    ATTENDANCE_STATUSES.MAKEUP_PRESENT,
    ATTENDANCE_STATUSES.MAKEUP_TARDY,
  ])

/**
 * Returns the projected project day for a student.
 * Assumes the student attends today — once marked attended the count already
 * includes today, so we only add 1 when statusToday is NOT an attended status.
 */
export const getProjectedProjectDay = (
  student: StudentConstructor,
  projectId: Uuid | undefined,
  statusToday: AttendanceStatus | undefined
): number => {
  const completedDays = projectId ? (student.projects?.[projectId] ?? 0) : 0

  const alreadyAttended =
    statusToday !== undefined && ATTENDED_PROJECT_STATUSES.has(statusToday)

  return completedDays + (alreadyAttended ? 0 : 1)
}

/**
 * A student is "on track" when the session has no project tracking info or
 * when their projected day has reached (or passed) session.projectFraction.
 * Only students who are BEHIND (projected day below the session's fraction)
 * are flagged as exceptions; caught-up and ahead students are on track.
 */
export const isStudentProjectOnTrack = (
  student: StudentConstructor,
  session: ClassSessionConstructor,
  statusToday: AttendanceStatus | undefined
): boolean => {
  if (session.projectFraction == null || !session.projectId) {
    return true
  }

  const projectedDay = getProjectedProjectDay(
    student,
    session.projectId,
    statusToday
  )

  return projectedDay >= session.projectFraction
}
